import i18n from '@/locales/i18n'
import partnerApi from '@/services/partners.service'
import { EStoreModules } from '@/store/storeType'
import type { PartnerModel } from '@/types/partners/PartnerModel'
import { EAlertActions } from '@rfc/rfc-components-library/store'
import {
    EHandler,
    EPaginationOptions,
    type PaginationDataType,
    type PaginationType
} from '@rfc/rfc-components-library/types'
import { getPageSize, setDocumentAndTitle } from '@rfc/rfc-components-library/utils'
import { ref, type Ref } from 'vue'
import type { Router } from 'vue-router'
import type { Commit, Dispatch } from 'vuex'
import { EPartnerActions, EPartnerMutations, type PartnerState } from './PartnerStoreTypes'

const state: Ref<PaginationDataType<PartnerState>> = ref({
    data: { partners: [] },
    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    loading: false
})

const getters = {
    getPartners: () => state.value.data?.partners,
    loading: () => state.value.loading
}

const actions = (router: Router) => ({
    async [EPartnerActions.FETCH_PARTNER]({ commit }: { commit: Commit }, partnerId: number) {
        commit(EPartnerMutations.SET_LOADING, true)
        return await partnerApi
            .getPartner(partnerId)
            .then(res => {
                commit(EPartnerMutations.SET_PARTNER, res.data)
                setDocumentAndTitle(router, res.data.name)
                return res.data
            })
            .finally(() => commit(EPartnerMutations.SET_LOADING, false))
    },

    async [EPartnerActions.FETCH_PARTNERS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType> & { noQueryParams: boolean }
    ) {
        commit(EPartnerMutations.SET_LOADING, true)
        const { noQueryParams, ...other } = params
        partnerApi
            .getPartners(other, noQueryParams)
            .then(data => {
                commit(EPartnerMutations.SET_PARTNERS, data.data)
                commit(EPartnerMutations.SET_TOTAL, data)
            })
            .finally(() => commit(EPartnerMutations.SET_LOADING, false))
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`partner.api.success.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    },
    [EHandler.HANDLE_ERROR](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.ERROR,
                message: i18n.global.t(`partner.api.error.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    }
})

const mutations = {
    [EPartnerMutations.SET_PARTNERS](_: PartnerState, partners: any) {
        state.value.data!.partners = partners
    },

    [EPartnerMutations.SET_PARTNER](_: PartnerState, partner: any) {
        state.value.data!.partner = partner
    },

    [EPartnerMutations.SET_TOTAL](_: PartnerState, data: PaginationDataType<PartnerModel>) {
        state.value.current_page = data.current_page
        state.value.total = data.total
        state.value.last_page = data.last_page
        state.value.to = data.to
        state.value.from = data.from
        state.value.per_page = data.per_page
    },

    [EPartnerMutations.SET_LOADING](_: PartnerState, loading: boolean) {
        state.value.loading = loading
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
